import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { Box, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import {
  AssignmentIndOutlined as AssignmentIndOutlinedIcon,
  AttachFileOutlined as AttachFileOutlinedIcon,
  BusinessCenterOutlined as BusinessCenterOutlinedIcon,
  Circle as CircleIcon,
  ContentPasteOutlined as ContentPasteOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  FlashOnOutlined as FlashOnOutlinedIcon,
  ForwardOutlined as ForwardOutlinedIcon,
  Google as GoogleIcon,
  GridOnOutlined as GridOnOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  LiveHelpOutlined as LiveHelpOutlinedIcon,
  LoopOutlined as LoopOutlinedIcon,
  SendToMobile as SendToMobileIcon,
  Sms as SmsIcon,
  Tag as TagIcon,
  VerifiedOutlined as VerifiedOutlinedIcon
} from '@mui/icons-material';
import AccountClockIcon from '../EditJob/Sources/AccountClockIcon';
import SourcesIconSVG from '../EditJob/Sources/SourcesIconSVG';
import { RobotIcon } from '../../AIStudio/SharedComponents/Graphics/FelixSVG';
import { convertToNormalTabIndex, TabGroup } from './tabIndexUtils';
import { classes } from './styles';
import EditJobToggleLeftSVG from './EditJobToggleLeftSVG';
import EditJobToggleRightSVG from './EditJobToggleRightSVG';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';
import { IJob } from './types';
import { useFeatureFlag } from '../../FeatureFlags';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';

export default function EditJobTabs({
  editTabValue,
  handleEditTabChange,
  isToggled,
  setIsToggled,
  job,
  changes,
  setChanges
}: {
  editTabValue: number;
  handleEditTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  isToggled: boolean;
  setIsToggled: (isToggled: boolean) => void;
  job: IJob | null;
  changes: string[];
  setChanges: Dispatch<SetStateAction<string[]>>;
}) {
  const userPermissions = formatUserPermissions();
  const aiSummariesEnabled = useFeatureFlag('aiSummariesEnabled');
  const aiStudioEnabled = useFeatureFlag('aiStudioEnabled');

  const convertTabValueKey = (tabValue: number) => {
    switch (tabValue) {
      case 2:
        return 'description';
      case 3:
        return 'template';
      case 4:
        return 'questions';
      case 5:
        return 'sources';
      case 7:
        return 'attachments';
      case 9:
        return 'auto_status';
      case 10:
        return 'status_transitions';
      case 11:
        return 'auto_forward';
      case 12:
        return 'auto_email_reply';
      case 13:
        return 'auto_sms_reply';
      case 14:
        return 'auto_sms_forward';
      case 16:
        return 'requisition_info';
      case 17:
        return 'hr_questions';
      case 18:
        return 'automated_testgrid';
      case 19:
        return 'revolve';
      case 20:
        return 'google_conversion_script';
      case 21:
        return 'panel_workflow';
      case 22:
        return 'ai_studio';
      case 24:
        return 'auto_tag_conditions';
      default:
        return '';
    }
  };

  const TabProps = useCallback((tabIndex: number, group: number) => {
    const index = convertToNormalTabIndex(tabIndex, group);
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
      'data-testid': `vertical-tab-${index}`
    };
  }, []);

  const tabLabel = (label: string, value: number) => {
    return isToggled ? (
      ''
    ) : (
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          svg: { color: '#5BC4BF' }
        }}
      >
        {label}
        {changes.includes(convertTabValueKey(value)) && <CircleIcon sx={{ fontSize: '0.75rem' }} />}
      </Stack>
    );
  };

  return (
    <Tabs
      orientation="vertical"
      value={editTabValue}
      variant="scrollable"
      onChange={(event, newValue) => {
        const key = convertTabValueKey(newValue);
        const newChanges = changes.filter((change) => change !== key);
        setChanges(newChanges);
        handleEditTabChange(event, newValue);
      }}
      aria-label="Vertical tabs example"
      sx={{
        ...classes.editTabs,
        '& svg': {
          height: '1.5rem',
          marginRight: isToggled ? '0px !important' : '16px !important',
          color: '#084D6D'
        },
        '& .MuiTabs-indicator': { ...classes.editTabIndicator },
        minWidth: isToggled ? '58px !important' : '220px !important',
        transition: 'all 0.5s'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            ...classes.tabsSubheader,
            fontSize: isToggled ? 0 : '12px',
            transition: 'all 0.5s'
          }}
        >
          Job Information
        </Box>
        <Box
          sx={{ ...classes.toggleIcon, marginLeft: isToggled ? '6.5px' : 'auto' }}
          onClick={() => setIsToggled(!isToggled)}
        >
          {isToggled ? <EditJobToggleRightSVG /> : <EditJobToggleLeftSVG />}
        </Box>
      </Box>
      <Tooltip placement="right" title={'General'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<InfoOutlinedIcon />}
          iconPosition="start"
          label={tabLabel('General', 1)}
          {...TabProps(0, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Description'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<BusinessCenterOutlinedIcon />}
          iconPosition="start"
          label={tabLabel('Description', 2)}
          {...TabProps(1, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Template'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<ContentPasteOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Edit Job Template']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Template', 3)}
          {...TabProps(2, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Questions'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<LiveHelpOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Create / Edit Job Questions']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Questions', 4)}
          {...TabProps(3, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Sources'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SourcesIconSVG />}
          iconPosition="start"
          label={tabLabel('Sources', 5)}
          {...TabProps(4, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Candidate Settings
      </Box>
      <Tooltip placement="right" title={'Attachments'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<AttachFileOutlinedIcon />}
          iconPosition="start"
          label={tabLabel('Attachments', 7)}
          {...TabProps(0, TabGroup.CANDIDATE_SETTINGS)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Automation
      </Box>
      <Tooltip placement="right" title={'Auto Statuses'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={
            <FlashOnOutlinedIcon
              sx={{ color: 'transparent !important', stroke: '#084d6d', strokeWidth: '1.8px' }}
            />
          }
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Statuses']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Auto Statuses', 9)}
          {...TabProps(1, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Status Transitions'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<AccountClockIcon />}
          iconPosition="start"
          label={tabLabel('Status Transitions', 10)}
          {...TabProps(2, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto Forward'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<ForwardOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Auto Forward', 11)}
          {...TabProps(3, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto Email Reply'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<EmailOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Auto Email Reply', 12)}
          {...TabProps(4, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto SMS Reply'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SmsIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Auto SMS Reply', 13)}
          {...TabProps(5, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto SMS Forward'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SendToMobileIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Auto SMS Forward', 14)}
          {...TabProps(6, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Advanced
      </Box>
      <Tooltip placement="right" title={'Requisition Info'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<VerifiedOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions.Jobs?.['Migrate from Requisition Form']
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Requisition Info', 16)}
          {...TabProps(0, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Configure Application HR Questions'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<LiveHelpOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions.Jobs?.ConfigureApplicationHRQuestions
              ? 'block'
              : 'none !important'
          }}
          label={tabLabel('Configure Application HR Questions', 17)}
          {...TabProps(1, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Automated TestGrid'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<GridOnOutlinedIcon />}
          iconPosition="start"
          sx={{
            display:
              job?.integrations?.test_grid_configured && userPermissions.Integrations?.TestGrid
                ? 'block'
                : 'none !important'
          }}
          label={tabLabel('Automated TestGrid', 18)}
          {...TabProps(2, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Revolve'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<LoopOutlinedIcon />}
          iconPosition="start"
          sx={{
            display:
              job?.integrations?.revolve_configured &&
              userPermissions.Integrations?.['Export To Revolve Onboard']
                ? 'block'
                : 'none !important'
          }}
          label={tabLabel('Revolve', 19)}
          {...TabProps(3, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Google Conversion Script'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<GoogleIcon />}
          iconPosition="start"
          label={tabLabel('Google Conversion Script', 20)}
          {...TabProps(4, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Panel Workflow'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<AssignmentIndOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: job?.entity_settings.show_panel_workflow ? 'block' : 'none !important'
          }}
          label={tabLabel('Panel Workflow', 21)}
          {...TabProps(5, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'AI Studio'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<RobotIcon viewBox="0 0 19 19" />}
          iconPosition="start"
          sx={{
            display: aiStudioEnabled && aiSummariesEnabled ? 'block' : 'none !important'
          }}
          label={tabLabel('AI Studio', 22)}
          {...TabProps(6, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        New features
      </Box>
      <Tab
        icon={<TagIcon />}
        iconPosition="start"
        sx={{
          display: 'block'
        }}
        label={
          !isToggled && (
            <Stack
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                svg: { color: '#5BC4BF' }
              }}
            >
              <Stack>Auto Tags</Stack>
              {changes.includes('auto_tag_conditions') ? (
                <CircleIcon sx={{ fontSize: '0.75rem' }} />
              ) : (
                <Box sx={{ ...sharedClasses.applicationBetaButton, boxShadow: 'none' }}>NEW</Box>
              )}
            </Stack>
          )
        }
        {...TabProps(0, TabGroup.NEW_FEATURES)}
      />
    </Tabs>
  );
}
